import {useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useLocale} from "./useFetchTheatron";
import * as locales from "date-fns/locale";
import format from "date-fns/format";
import buildLocalizeFn from  "date-fns/locale/_lib/buildLocalizeFn";
import { dayPeriodValues, formattingDayPeriodValues } from '../constants/date';
import { intervalToDuration, formatDuration } from 'date-fns'

locales.zhTW.localize.dayPeriod = buildLocalizeFn({
    values: dayPeriodValues,
    defaultWidth: 'wide',
    formattingValues: formattingDayPeriodValues,
    defaultFormattingWidth: 'wide'
})

export const useTimer = (rawDate, rawStartDate= null) => {
    const [now, setNow] = useState(null);
    const [date, setDate] = useState(null);
    const [startDate, setStartDate] = useState(null);

    useEffect(() => {
        if(!rawDate) {
            return;
        }

        setNow(+(new Date));
        setDate(+(new Date(rawDate)));
        if(rawStartDate) {
            setStartDate(+(new Date(rawStartDate)));
        }

        const intervalRef = setInterval(() => {
            setNow(+(new Date));
        }, 1000);

        return () => {
            clearInterval(intervalRef);
        };
    }, [rawDate, rawStartDate]);

    return useMemo(() => {
        if(!date || !now) {
            return null;
        }
        const difference = date && now ? date - now : 0;

        if (difference > 0) {
            return {
                days: Math.floor(difference / (1000 * 60 * 60 * 24)),
                hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
                minutes: Math.floor((difference / 1000 / 60) % 60),
                seconds: Math.floor((difference / 1000) % 60),
                percentage: startDate ? ((now - startDate) / (date - startDate)) * 100  : null
            };
        }
        else {
            return {days: 0, hours: 0, minutes: 0, seconds: 0, percentage: 0};
        }
    }, [now, date]);
};

export const useFormatDate = () => {
  const {t, i18n} = useTranslation('date');

  return useMemo(() => {
    // TODO: Need to standardize this.. store in i18n as full locale string instead
    const locale = {
      de: locales.de,
      zh: locales.zhTW,
      en: locales.enGB,
    }[i18n.language] || locales.enGB;

    return {
      format: (date, formatString = t('full-datetime')) => {
        return format(new Date(date), formatString, {
          locale,
        })
      },
      formatDate: (date) => {
        return format(new Date(date), t('format-date'), {
          locale,
        })
      },
      formatTime: (date) => {
        return format(new Date(date), t('time'), {
          locale,
        })
      },
      formatRange: (startDate, endDate) => {
        if (!startDate) {
          return '';
        }
        const start = new Date(startDate);
        const startString = format(start, 'y.MM.dd');
        if (!endDate) {
          return startString;
        }
        const end = new Date(endDate);
        const endString = format(end, 'y.MM.dd');
        if (startString === endString) {
          return startString;
        }
        return startString.concat('–', (end.getFullYear() === start.getFullYear() ? format(end, 'MM.dd') : endString));
      },
      formatDuration: (startDate, endDate) => {
        const start = new Date(startDate);
        const end = new Date(endDate);
        const duration = formatDuration(intervalToDuration({
          start: start,
          end: end
        }));
        return duration;
      }
    }
  }, [i18n.language]);
}
